export type SensitiveActionDisplayDefinition = {
  auditLabel: string;
  confirmationVerbPhrase: string;
  prompt?: string;
  reason?: string;
};

export const sensitiveActionDisplayDefinitions: Record<string, SensitiveActionDisplayDefinition> = {
  'admin.financing_type_exclusivity.update': {
    auditLabel: 'Update financier eligibility rules',
    confirmationVerbPhrase: 'update financier eligibility rules',
    prompt: 'Confirm it is you before changing which financiers receive matching opportunities.',
    reason: 'This change affects lender routing across SportsFi and should only be made by a verified admin.',
  },
  'admin.league_rule.create': {
    auditLabel: 'Create a league rule',
    confirmationVerbPhrase: 'create a league rule',
  },
  'admin.league_rule.delete': {
    auditLabel: 'Delete a league rule',
    confirmationVerbPhrase: 'delete a league rule',
  },
  'admin.league_rule.update': {
    auditLabel: 'Update a league rule',
    confirmationVerbPhrase: 'update a league rule',
  },
  'admin.user.create': {
    auditLabel: 'Create a user account',
    confirmationVerbPhrase: 'create a user account',
  },
  'admin.user.deactivate': {
    auditLabel: 'Deactivate a user account',
    confirmationVerbPhrase: 'deactivate this user account',
    prompt: 'Confirm it is you before blocking this user from accessing SportsFi.',
    reason: 'Deactivating users blocks platform access and should only be done by a verified admin.',
  },
  'admin.user.mfa_reset': {
    auditLabel: 'Reset user MFA',
    confirmationVerbPhrase: "reset this user's MFA",
    prompt: "Confirm it is you before resetting this user's MFA.",
    reason: 'Resetting MFA removes account security credentials, signs the user out, and forces fresh setup.',
  },
  'admin.user.password_reset': {
    auditLabel: 'Send a password reset email',
    confirmationVerbPhrase: 'send a password reset email',
  },
  'admin.user.reactivate': {
    auditLabel: 'Reactivate a user account',
    confirmationVerbPhrase: 'reactivate this user account',
    prompt: 'Confirm it is you before restoring this user account.',
    reason: 'Reactivating users restores platform access and should only be done by a verified admin.',
  },
  'admin.user.update': {
    auditLabel: 'Update a user account',
    confirmationVerbPhrase: 'update a user account',
  },
  'application.resubmit': {
    auditLabel: 'Resubmit a financing application',
    confirmationVerbPhrase: 'resubmit this financing application',
  },
  'application.submit': {
    auditLabel: 'Submit a financing application',
    confirmationVerbPhrase: 'submit this financing application',
  },
  'application_request.accept': {
    auditLabel: 'Accept an application request',
    confirmationVerbPhrase: 'accept this application request',
  },
  'application_request.create': {
    auditLabel: 'Create an application request',
    confirmationVerbPhrase: 'create this application request',
  },
  'application_request.reject': {
    auditLabel: 'Reject an application request',
    confirmationVerbPhrase: 'reject this application request',
  },
  'application_request_further_detail.confirm_receipt': {
    auditLabel: 'Confirm receipt of further detail',
    confirmationVerbPhrase: 'confirm receipt of further detail',
  },
  'application_request_further_detail.send': {
    auditLabel: 'Send a further-detail request',
    confirmationVerbPhrase: 'send this further-detail request',
  },
  'application_request_further_detail.submit': {
    auditLabel: 'Submit a further-detail response',
    confirmationVerbPhrase: 'submit this further-detail response',
  },
  'contract.reject': {
    auditLabel: 'Reject a contract',
    confirmationVerbPhrase: 'reject this contract',
  },
  'contract.send': {
    auditLabel: 'Send a contract',
    confirmationVerbPhrase: 'send this contract',
  },
  'invite.approve_provisional': {
    auditLabel: 'Approve a provisional invite',
    confirmationVerbPhrase: 'approve this provisional invite',
  },
  'invite.create': {
    auditLabel: 'Create an invite',
    confirmationVerbPhrase: 'create this invite',
  },
  'invite.resend': {
    auditLabel: 'Resend an invite',
    confirmationVerbPhrase: 'resend this invite',
  },
  'invite.withdraw': {
    auditLabel: 'Withdraw an invite',
    confirmationVerbPhrase: 'withdraw this invite',
  },
  'organisation.financier_profile.update': {
    auditLabel: 'Update a financier profile',
    confirmationVerbPhrase: 'update this financier profile',
  },
  'organisation.profile.update': {
    auditLabel: 'Update an organisation profile',
    confirmationVerbPhrase: 'update this organisation profile',
  },
  'organisation.user.remove': {
    auditLabel: 'Remove an organisation user',
    confirmationVerbPhrase: 'remove this organisation user',
    prompt: 'Confirm it is you before removing this member from your organisation.',
    reason: 'Are you sure you want to remove this member from your organisation? This action cannot be undone.',
  },
  'security.credential.remove': {
    auditLabel: 'Remove a sign-in method',
    confirmationVerbPhrase: 'remove this sign-in method',
  },
  'transfer_deal.document.accept': {
    auditLabel: 'Accept a transfer deal document',
    confirmationVerbPhrase: 'accept this transfer deal document',
  },
  'transfer_deal.enter_execution': {
    auditLabel: 'Enter transfer deal execution',
    confirmationVerbPhrase: 'enter this transfer deal for execution',
  },
  'transfer_deal.execute': {
    auditLabel: 'Execute a transfer deal',
    confirmationVerbPhrase: 'execute this transfer deal',
  },
  'transfer_deal.setup': {
    auditLabel: 'Set up a transfer deal',
    confirmationVerbPhrase: 'set up this transfer deal',
  },
};

export const sensitiveActionRequiredLevelLabels: Record<string, string> = {
  admin_mfa: 'Admin MFA required',
  mfa: 'MFA required',
  passkey_mfa: 'Passkey required',
  password: 'Fresh sign-in required',
};

export const sensitiveActionSecurityLevelLabels: Record<string, string> = {
  admin_mfa: 'Admin MFA',
  mfa: 'MFA',
  passkey_mfa: 'Passkey MFA',
  password: 'Fresh sign-in',
};

const formatUnknownActionId = (actionId: string): string =>
  actionId
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const formatSensitiveActionLabel = (actionId: string): string =>
  sensitiveActionDisplayDefinitions[actionId]?.auditLabel ?? formatUnknownActionId(actionId);

export const formatSensitiveActionConfirmationVerbPhrase = (actionId: string): string =>
  sensitiveActionDisplayDefinitions[actionId]?.confirmationVerbPhrase ?? 'continue';

export const formatSensitiveActionPrompt = (actionId: string): string =>
  sensitiveActionDisplayDefinitions[actionId]?.prompt ??
  `Confirm it is you before you ${formatSensitiveActionConfirmationVerbPhrase(actionId)}.`;

export const formatSensitiveActionReason = (
  actionId: string,
  fallbackReason: string | null | undefined
): string | null => sensitiveActionDisplayDefinitions[actionId]?.reason ?? fallbackReason ?? null;

export const formatSensitiveActionRequiredLevelLabel = (level: string | null | undefined): string =>
  level != null
    ? (sensitiveActionRequiredLevelLabels[level] ?? 'Additional verification required')
    : 'Additional verification required';

export const formatSensitiveActionSecurityLevelLabel = (level: string | null | undefined): string =>
  level != null ? (sensitiveActionSecurityLevelLabels[level] ?? 'Additional verification') : 'Additional verification';
